import React, { useState, useEffect } from 'react';
import { Crown, Trophy, TrendingUp, Star, Award, Medal, Target, Zap } from 'lucide-react';

const RecordBookBanner = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showImage, setShowImage] = useState(false);

  useEffect(() => {
    // Start showing the image after component mounts - increased delay for more dramatic effect
    const timer = setTimeout(() => {
      setShowImage(true);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div className="relative bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 rounded-2xl shadow-xl overflow-hidden">
      {/* Enhanced Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-1/4 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-1/4 w-24 h-24 bg-yellow-400/10 rounded-full blur-lg animate-bounce"></div>
        <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-purple-400/10 rounded-full blur-md animate-pulse delay-300"></div>
        
        {/* Additional Floating Particles */}
        <div className="absolute top-10 right-10 w-6 h-6 bg-amber-400/30 rounded-full animate-bounce delay-500"></div>
        <div className="absolute bottom-10 left-20 w-4 h-4 bg-yellow-400/40 rounded-full animate-ping"></div>
        <div className="absolute top-1/2 left-10 w-8 h-8 bg-gold-400/20 rounded-full animate-pulse delay-700"></div>
        <div className="absolute bottom-1/3 right-1/2 w-5 h-5 bg-amber-400/35 rounded-full animate-bounce delay-1000"></div>
        
        {/* Record-themed floating elements */}
        <div className="absolute top-16 right-1/4 w-10 h-10 bg-yellow-400/20 rounded-lg animate-bounce delay-200 rotate-12"></div>
        <div className="absolute bottom-16 left-1/3 w-6 h-6 bg-amber-400/30 rounded-full animate-pulse delay-600"></div>
        <div className="absolute top-2/3 right-20 w-8 h-8 bg-gold-400/25 rounded-lg animate-bounce delay-800 -rotate-12"></div>
      </div>

      {/* Subtle Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"
             style={{
               backgroundImage: `radial-gradient(circle at 20px 20px, rgba(255,255,255,0.1) 1px, transparent 1px)`,
               backgroundSize: '40px 40px'
             }}>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 p-8">
        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-4 gap-8 items-center min-h-[200px]">
          {/* Left Side - Beast Diving Image with Premium Fade-in */}
          <div className="col-span-1 flex justify-center items-center h-full">
            <div className="relative">
              {/* Placeholder shimmer effect while loading */}
              <div className={`absolute inset-0 bg-gradient-to-r from-white/10 via-white/20 to-white/10 rounded-full transition-opacity duration-2000 ${
                imageLoaded ? 'opacity-0' : 'opacity-100 animate-pulse'
              }`} 
              style={{ 
                width: '18rem', 
                height: '18rem',
                transform: 'translateY(-8px)'
              }}></div>
              
              <img 
                src="/beastdiving.png" 
                alt="Beast Diving" 
                className={`w-56 h-56 lg:w-72 lg:h-72 object-contain drop-shadow-2xl transition-all duration-3500 ease-out ${
                  showImage && imageLoaded 
                    ? 'opacity-90 scale-100 translate-y-0' 
                    : 'opacity-0 scale-90 translate-y-4'
                }`}
                style={{ 
                  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))',
                  transform: showImage && imageLoaded 
                    ? 'translateY(-8px) scale(1)' 
                    : 'translateY(12px) scale(0.90)',
                  maskImage: 'radial-gradient(ellipse at center, black 30%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0.4) 70%, transparent 85%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0.4) 70%, transparent 85%)'
                }}
                onLoad={handleImageLoad}
              />
              
              {/* Enhanced glowing effect with fade-in */}
              <div className={`absolute inset-0 bg-white/20 blur-3xl rounded-full scale-75 -z-10 transition-all duration-3500 ease-out delay-1000 ${
                showImage && imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}></div>
              
              {/* Additional glow layers for premium effect */}
              <div className={`absolute inset-0 bg-purple-400/15 blur-2xl rounded-full scale-90 -z-20 transition-all duration-4000 ease-out delay-1500 ${
                showImage && imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}></div>
            </div>
          </div>

          {/* Center - Text Content */}
          <div className="col-span-2 text-center">
            <div className="flex items-center justify-center mb-6">
              <div>
                <h1 className="text-4xl lg:text-5xl font-black text-white mb-3 tracking-tight">
                  Record Book
                </h1>
                <p className="text-purple-100 text-lg lg:text-xl font-medium leading-relaxed">
                  The complete collection of Beasts of Bellevue achievements and milestones
                </p>
                
                {/* Feature highlights */}
                <div className="flex items-center justify-center space-x-6 mt-6">
                  <div className="flex items-center space-x-2 text-purple-100">
                    <Trophy className="h-5 w-5 text-yellow-300" />
                    <span className="text-sm font-medium">Historic Records</span>
                  </div>
                  <div className="flex items-center space-x-2 text-purple-100">
                    <Target className="h-5 w-5 text-yellow-300" />
                    <span className="text-sm font-medium">Epic Achievements</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Empty space to match other banners */}
          <div className="col-span-1"></div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden relative min-h-[200px] flex items-center justify-center">
          {/* Background Beast Diving Image with Premium Fade-in */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Mobile placeholder shimmer */}
            <div className={`absolute bg-gradient-to-r from-white/5 via-white/10 to-white/5 rounded-full transition-opacity duration-2000 ${
              imageLoaded ? 'opacity-0' : 'opacity-100 animate-pulse'
            }`} 
            style={{ 
              width: '24rem', 
              height: '24rem'
            }}></div>
            
            <img 
              src="/beastdiving.png" 
              alt="Beast Diving" 
              className={`w-88 h-88 sm:w-104 sm:h-104 object-contain transition-all duration-4000 ease-out ${
                showImage && imageLoaded 
                  ? 'opacity-30 scale-100' 
                  : 'opacity-0 scale-85'
              }`}
              style={{ 
                filter: 'blur(1px)',
                maskImage: 'radial-gradient(ellipse at center, black 25%, rgba(0,0,0,0.7) 45%, rgba(0,0,0,0.3) 65%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(ellipse at center, black 25%, rgba(0,0,0,0.7) 45%, rgba(0,0,0,0.3) 65%, transparent 80%)'
              }}
              onLoad={handleImageLoad}
            />
          </div>
          
          {/* Mobile Text Content */}
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-3 tracking-tight">
              Record Book
            </h1>
            <p className="text-purple-100 text-base sm:text-lg font-medium leading-relaxed max-w-md mx-auto">
              The complete collection of Beasts of Bellevue achievements and milestones
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};


export default RecordBookBanner;